import { useCallback, useEffect, useMemo, useState } from 'react';
import type { AuthRevalidationFn } from '../lib/auth';
import type { SupportedSymbol } from '../lib/symbols';
import {
  ALL_WATCHLIST_ID,
  BUILT_IN_WATCHLISTS,
  INDEXES_WATCHLIST_ID,
  isBuiltInWatchlistId,
  readActiveWatchlistPreference,
  writeActiveWatchlistPreference,
} from '../lib/watchlists';
import type { Ticker } from '../store/heatmapStore';
import { useWatchlists } from './useWatchlists';

const EMPTY_WATCHLISTS = [] as const;

interface UseWatchlistSelectionInput {
  userId: string | null;
  activeTicker: Ticker;
  symbols: readonly SupportedSymbol[];
  enabled: boolean;
  loadPersonalWatchlists: boolean;
  onTickerChange: (ticker: Ticker) => void;
  onAuthRevalidationRequested: AuthRevalidationFn;
}

export function useWatchlistSelection({
  userId,
  activeTicker,
  symbols,
  enabled,
  loadPersonalWatchlists,
  onTickerChange,
  onAuthRevalidationRequested,
}: UseWatchlistSelectionInput) {
  const [requestedWatchlistId, setRequestedWatchlistId] = useState(() =>
    readActiveWatchlistPreference(userId),
  );
  const watchlistState = useWatchlists(
    onAuthRevalidationRequested,
    enabled && loadPersonalWatchlists,
  );
  const watchlists = useMemo(
    () =>
      watchlistState.collection?.watchlists.filter(
        (watchlist) => !watchlist.id.startsWith('__pending_watchlist_'),
      ) ?? EMPTY_WATCHLISTS,
    [watchlistState.collection?.watchlists],
  );
  const enabledSymbols = useMemo(() => symbols.filter((symbol) => symbol.enabled), [symbols]);
  const enabledSymbolsByWatchlist = useMemo(() => {
    const enabledSymbolsByTicker = new Map(
      enabledSymbols.map((symbol) => [symbol.ticker, symbol] as const),
    );
    const builtInEntries = BUILT_IN_WATCHLISTS.map((watchlist) => {
      const watchlistSymbols =
        watchlist.tickers == null
          ? enabledSymbols
          : watchlist.tickers
              .map((ticker) => enabledSymbolsByTicker.get(ticker))
              .filter((symbol): symbol is SupportedSymbol => symbol != null);
      return [watchlist.id, watchlistSymbols] as const;
    });

    return new Map([
      ...builtInEntries,
      ...watchlists.map(
        (watchlist) =>
          [
            watchlist.id,
            watchlist.symbols
              .map((symbol) => enabledSymbolsByTicker.get(symbol.ticker))
              .filter((symbol): symbol is SupportedSymbol => symbol != null),
          ] as const,
      ),
    ]);
  }, [enabledSymbols, watchlists]);
  const fallbackWatchlistId =
    (enabledSymbolsByWatchlist.get(INDEXES_WATCHLIST_ID)?.length ?? 0) > 0
      ? INDEXES_WATCHLIST_ID
      : ALL_WATCHLIST_ID;

  const activeWatchlistId = useMemo(() => {
    if (!enabled) {
      return ALL_WATCHLIST_ID;
    }
    if (requestedWatchlistId === ALL_WATCHLIST_ID) {
      return requestedWatchlistId;
    }
    const available = enabledSymbolsByWatchlist.get(requestedWatchlistId);
    if (isBuiltInWatchlistId(requestedWatchlistId)) {
      return available != null && available.length > 0 ? requestedWatchlistId : fallbackWatchlistId;
    }
    if (watchlistState.collection == null) {
      return fallbackWatchlistId;
    }
    return available != null && available.length > 0 ? requestedWatchlistId : fallbackWatchlistId;
  }, [
    enabled,
    enabledSymbolsByWatchlist,
    fallbackWatchlistId,
    requestedWatchlistId,
    watchlistState.collection,
  ]);

  const activeSymbols = enabledSymbolsByWatchlist.get(activeWatchlistId) ?? enabledSymbols;
  const selectedTicker = useMemo(() => {
    if (activeSymbols.some((symbol) => symbol.ticker === activeTicker)) {
      return activeTicker;
    }
    return activeSymbols[0]?.ticker ?? activeTicker;
  }, [activeSymbols, activeTicker]);
  const symbolCounts = useMemo(
    () =>
      new Map(
        [...enabledSymbolsByWatchlist].map(([watchlistId, watchlistSymbols]) => [
          watchlistId,
          watchlistSymbols.length,
        ]),
      ),
    [enabledSymbolsByWatchlist],
  );

  useEffect(() => {
    if (!enabled) {
      return;
    }
    if (isBuiltInWatchlistId(requestedWatchlistId) || watchlistState.collection != null) {
      writeActiveWatchlistPreference(userId, activeWatchlistId);
    }
  }, [activeWatchlistId, enabled, requestedWatchlistId, userId, watchlistState.collection]);

  const selectWatchlist = useCallback(
    (watchlistId: string) => {
      const requestedSymbols = enabledSymbolsByWatchlist.get(watchlistId);
      const nextWatchlistId =
        watchlistId === ALL_WATCHLIST_ID ||
        (requestedSymbols != null && requestedSymbols.length > 0)
          ? watchlistId
          : fallbackWatchlistId;
      const nextSymbols = enabledSymbolsByWatchlist.get(nextWatchlistId) ?? enabledSymbols;
      const nextTicker = nextSymbols.some((symbol) => symbol.ticker === selectedTicker)
        ? selectedTicker
        : nextSymbols[0]?.ticker;

      setRequestedWatchlistId(nextWatchlistId);
      if (nextTicker != null && nextTicker !== activeTicker) {
        onTickerChange(nextTicker);
      }
    },
    [
      activeTicker,
      enabledSymbols,
      enabledSymbolsByWatchlist,
      fallbackWatchlistId,
      onTickerChange,
      selectedTicker,
    ],
  );

  return {
    watchlistState,
    watchlists,
    activeWatchlistId,
    activeSymbols,
    selectedTicker,
    symbolCounts,
    selectWatchlist,
  };
}
